import React, { useEffect, useState } from 'react';
import { Alert, Form, Input, Modal } from 'antd';

import { useAccess } from '../../../auth/AccessContext.jsx';
import { StepUpCancelled, useStepUp } from '../../../stepup/useStepUp.js';
import { REASON_MAX, approveInBatches, approvalBatches, reasonProblem, reasonRequest } from './orders.js';
import { repricedSinceReviewRefs } from './orderRows.js';

/**
 * Centered confirmation for a pending order's Approve or Reject — the same
 * shape as the Console's other confirms. Approve posts the order (and the
 * other leg of a switch or transfer, when it was gathered with it). Reject
 * needs the checker's reason (ORDER_REJECT, 3 to 500 characters on one line)
 * and takes an order in any open state (IP-130); the server rejects the other
 * leg with it. An order the client has not finished in LIV is never here
 * (IP-177).
 * IP-199: both need the checker's authenticator code. A reject asks once
 * (ORDER_REJECT, bound to the ids and the reason). An approval asks once per
 * request it sends (ORDER_APPROVE, bound to that request's ids and versions):
 * one code for the usual approval, another for each further batch or retry.
 * Money in clearing is released as the lapse releases it; money that has
 * cleared is flagged for a refund.
 *
 * @param {{ kind: 'approve'|'reject'|null, orders: object[], onClose: () => void, onDone: (kind: string, result: object) => void }} props
 */
const ActionConfirmModal = ({ kind, orders, onClose, onDone }) => {
    const { api } = useAccess();
    const { withStepUp, stepUpDialog } = useStepUp();
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const order = orders[0];
    const paired = orders.length > 1 || Boolean(order?.linkedOrderId);
    // IP-197: approving is allowed, but the checker is told which were priced again after their review.
    const repriced = repricedSinceReviewRefs(orders);

    useEffect(() => {
        if (kind) setError(null);
    }, [kind]);

    const approve = async () => {
        setBusy(true);
        setError(null);
        // Set when the checker closes the code dialog: approveInBatches stops there, as after any refusal.
        let cancelled = false;
        const approveWithCode = async (orderIds, expectedVersions) => {
            try {
                return await withStepUp('ORDER_APPROVE', { orderIds, expectedVersions },
                    (stepUp) => api.orders.approve(orderIds, expectedVersions, stepUp));
            } catch (e) {
                if (e instanceof StepUpCancelled) cancelled = true;
                throw e;
            }
        };
        try {
            const result = await approveInBatches(approveWithCode, approvalBatches(orders));
            const nothingDone = !result.approved.length && !result.skipped.length && !result.failed.length;
            // Cancelled before anything was approved: close quietly, nothing was sent.
            if (cancelled && nothingDone) return;
            if (result.error && nothingDone) {
                setError(result.error.message);
                return;
            }
            onDone('approve', result);
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const reject = async ({ reason }) => {
        const { body, message } = reasonRequest('reject', orders, reason);
        if (!body) {
            setError(message);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            onDone('reject', await withStepUp('ORDER_REJECT', body,
                (stepUp) => api.orders.reject(body.orderIds, body.reason, stepUp)));
        } catch (e) {
            // Closing the code dialog cancels quietly: nothing was sent.
            if (e instanceof StepUpCancelled) return;
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const title = !order ? ''
        : kind === 'approve'
            ? orders.length === 1
                ? 'Approve ' + order.transactionRef + '?'
                : 'Approve ' + orders.length + ' transactions?'
            : orders.length === 1
                ? 'Reject ' + order.transactionRef + '?'
                : 'Reject ' + orders.length + ' transactions?';

    return (
        <>
            <Modal
                open={Boolean(kind) && Boolean(order)}
                centered
                title={title}
                okText={kind === 'reject' ? 'Reject' : 'Approve'}
                cancelText="Cancel"
                okButtonProps={{ danger: kind === 'reject', loading: busy }}
                cancelButtonProps={{ disabled: busy }}
                onOk={() => (kind === 'reject' ? form.submit() : approve())}
                onCancel={onClose}
                rootClassName="lfoc-styles-wrapper"
                destroyOnHidden
            >
                {/* IP-202: the strategy activation code of each transaction being decided. */}
                {order && (
                    <p className="action-confirm-strategy">
                        <span className="review-dialog-label">{orders.length === 1 ? 'Strategy code' : 'Strategy codes'}</span>{' '}
                        <span className="txn-id">
                            {orders.length === 1
                                ? order.strategyActivationCode ?? '—'
                                : orders.map((o) => o.transactionRef + ': ' + (o.strategyActivationCode ?? '—')).join(', ')}
                        </span>
                    </p>
                )}
                {kind === 'approve' && (
                    <p>
                        Units are allocated at the dealing date&apos;s price and posted to the register.
                        {orders.length > 1
                            ? ' Each transaction is approved at its dealing date\'s price.'
                            : paired && ' The other leg of this switch or transfer is approved with it.'}
                    </p>
                )}
                {kind === 'reject' && (
                    <>
                        <p>
                            Rejected transactions are never dealt, and LIV tells the investor your reason.
                            {orders.length > 1
                                ? ' The other leg of each switch or transfer is rejected with it.'
                                : paired && ' The other leg of this switch or transfer is rejected with it.'}
                            {' '}A subscription whose money has cleared is flagged for a refund; money still in clearing is
                            released as an unallocated receipt.
                        </p>
                        {error && <Alert type="error" showIcon title={error} className="auth-alert" />}
                        <Form form={form} layout="vertical" onFinish={reject} requiredMark={false}>
                            <Form.Item
                                name="reason"
                                label="Reason"
                                rules={[{ validator: (_, value) => (reasonProblem(value) ? Promise.reject(new Error(reasonProblem(value))) : Promise.resolve()) }]}
                            >
                                <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={REASON_MAX} showCount />
                            </Form.Item>
                        </Form>
                    </>
                )}
                {kind === 'approve' && repriced.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        className="auth-alert price-changed-alert"
                        title={(repriced.length === 1 ? 'Priced again since its last review: ' : 'Priced again since their last review: ') + repriced.join(', ')}
                        description="The review was before the latest pricing. Check the new figures in the transaction's history before you approve."
                    />
                )}
                {kind === 'approve' && error && <Alert type="error" showIcon title={error} className="auth-alert" />}
            </Modal>
            {stepUpDialog}
        </>
    );
};

export default ActionConfirmModal;
